import React from 'react';
import { Button, Space, Tag, Tooltip } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import HtmlParser from 'react-html-parser';
import AppointmentTimeCell from '@/components/appointments/AppointmentTimeCell';
import CustomerInfoColumn from '@/components/customers/CustomerInfoColumn';
import PhoneNumberList from '@/components/PhoneNumberList';
import { checkPermission } from '@/utils/permission';
import AppointmentStatusButton from '@/components/appointments/AppointmentStatusButton';
import AppointmentStaffCell from '@/components/appointments/AppointmentStaffCell';
import BirthdayCell from '@/components/customers/BirthdayCell';
import AppointmentServicesCell from '@/components/appointments/AppointmentServicesCell';
import {
    formatTimeWithH,
    isValidDate,
    renderAppointmentName,
    renderAppointmentStatus,
} from '@/utils/helpers';

const getNonEmptyNames = (items, getName) => (
    (items || [])
        .map(getName)
        .filter(Boolean)
);

const normalizeTreatmentSessions = (treatmentSessions) => {
    if (!treatmentSessions) return [];
    return Array.isArray(treatmentSessions) ? treatmentSessions : [treatmentSessions];
};

const treatmentStyles = {
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        lineHeight: 1.5,
        fontSize: 14,
        color: '#262626',
    },
    session: {
        padding: '6px 0',
        borderBottom: '1px solid #f0f0f0',
    },
    doctorRole: {
        color: '#999',
        fontSize: 14,
    },
    doctorName: {
        fontSize: 14,
        fontWeight: 700,
    },
    summaryLine: {
        marginTop: 5,
    },
    summaryLabel: {
        color: '#595959',
        fontWeight: 600,
        fontSize: 14,
        marginRight: 4,
    },
    summaryText: {
        color: '#262626',
        fontSize: 14,
    },
    tags: {
        display: 'inline-flex',
        flexWrap: 'wrap',
        gap: 4,
        verticalAlign: 'top',
    },
    itemTag: {
        margin: 0,
        whiteSpace: 'normal',
        lineHeight: 1.45,
        fontSize: 13,
        border: 0
    },
    empty: {
        color: '#bfbfbf',
        fontSize: 14,
    },
};

const renderTreatmentDoctor = (doctor) => {
    if (!doctor?.full_name) return <span style={treatmentStyles.empty}>----</span>;

    const roleName = doctor.user_rolename || doctor.user_role?.name || '';

    return (
        <div>
            {roleName && (
                <span style={treatmentStyles.doctorRole}>
                    {roleName} -{' '}
                </span>
            )}
            <span style={treatmentStyles.doctorName}>{doctor.full_name}</span>
        </div>
    );
};

const renderCompactLine = (label, items) => (
    items.length ? (
        <div style={treatmentStyles.summaryLine}>
            <span style={treatmentStyles.summaryLabel}>{items.length} {label}:</span>
            <span style={treatmentStyles.tags}>
                {items.map((item, index) => (
                    <Tag key={`${item}-${index}`} color="blue" style={treatmentStyles.itemTag}>
                        {item}
                    </Tag>
                ))}
            </span>
        </div>
    ) : null
);

const renderTextLine = (label, value) => (
    value ? (
        <div style={treatmentStyles.summaryLine}>
            <span style={treatmentStyles.summaryLabel}>{label}:</span>
            <span style={treatmentStyles.summaryText}>{value}</span>
        </div>
    ) : null
);

const renderTreatmentTicket = (record) => {
    const treatmentSessions = normalizeTreatmentSessions(
        record?.treatment_sessions || record?.treatmentSessions
    );

    if (!treatmentSessions.length) {
        return <span style={treatmentStyles.empty}>----</span>;
    }

    return (
        <div style={treatmentStyles.wrapper}>
            {treatmentSessions.map((session, index) => {
                const procedures = getNonEmptyNames(
                    session?.procedures,
                    (procedure) => procedure?.name
                );
                const services = getNonEmptyNames(
                    session?.customer_services || session?.customerServices,
                    (item) => item?.customer_service?.service?.name || item?.customerService?.service?.name
                );
                const note = session?.ghi_chu_noi_bo;
                const treatmentDate = isValidDate(session?.ngay, false);
                const hasContent = treatmentDate || note || session?.doctor?.full_name || procedures.length || services.length;

                return (
                    <div
                        key={session?.id || index}
                        style={{
                            ...treatmentStyles.session,
                            borderBottom: index === treatmentSessions.length - 1 ? 0 : treatmentStyles.session.borderBottom,
                        }}
                    >
                        {hasContent ? (
                            <>
                                {renderTextLine('Ngày điều trị', treatmentDate)}
                                {session?.doctor?.full_name && renderTreatmentDoctor(session?.doctor)}
                                {renderCompactLine('thủ thuật', procedures)}
                                {renderCompactLine('dịch vụ', services)}
                                {note && (
                                    <div style={treatmentStyles.summaryLine}>
                                        <span style={treatmentStyles.summaryLabel}>Ghi chú:</span>
                                        <span style={treatmentStyles.summaryText}>{HtmlParser(note)}</span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <span style={treatmentStyles.empty}>----</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const renderNextAppointment = (nextAppointment) => {
    if (!nextAppointment?.scheduled_at) return null;

    const appointmentName = renderAppointmentName(nextAppointment.name);
    const appointmentStatus = renderAppointmentStatus(nextAppointment.status, true);

    return (
        <Tooltip
            title={(
                <div>
                    {appointmentName && <div>Loại lịch: {appointmentName}</div>}
                    {appointmentStatus && <div>Trạng thái: {appointmentStatus}</div>}
                </div>
            )}
        >
            <div style={{ lineHeight: '1.2em' }}>
                <div style={{ color: '#006179', fontWeight: 500 }}>
                    <CalendarOutlined style={{ marginRight: 6 }} />
                    {isValidDate(nextAppointment.scheduled_at, false)}
                </div>
                <div style={{ color: '#1890ff', fontWeight: 500 }}>
                    <ClockCircleOutlined style={{ marginRight: 6 }} />
                    {formatTimeWithH(nextAppointment.scheduled_at)}
                    {appointmentName && (
                        <span style={{ fontSize: 12, color: '#666' }}>
                            {' '}({appointmentName})
                        </span>
                    )}
                </div>
            </div>
        </Tooltip>
    );
};

export const appointmentColumns = ({
    onDetail,
    onEdit,
    openStatusUpdateModal,
}) =>
    [
        {
            title: 'STT',
            align: 'center',
            width: '50px',
            dataIndex: 'stt',
            key: 'stt',
            render: (text, record, index) => {
                return index + 1;
                //return record.id
            }
        },
        {
            title: 'Giờ',
            dataIndex: 'scheduled_at',
            key: 'scheduled_at',
            width: '180px',
            render: (_, record) => (
                <AppointmentTimeCell record={record} />
            )
        },
        {
            title: 'Khách hàng',
            key: 'customer',
            width: 300,
            render: (_, record) => {
                const customer = {
                    id: record?.customer?.id,
                    code: record?.customer?.code,
                    full_name: record?.customer?.full_name,
                    branch_name: record?.customer?.branch?.name,
                    conditions: record?.customer?.condition_names_string,
                    customer_source: record?.customer?.customer_source?.name,
                    customer_status: record?.customer?.customer_status?.name,
                    customer_status_color: record?.customer?.customer_status?.text_color,
                    medical_book_number: record?.customer?.medical_book_number
                }
                return (
                    <CustomerInfoColumn
                        record={customer}
                        nameField="full_name"
                        idField="id"
                        codeField="code"
                        branchField="branch_name"
                        medicalBookField="medical_book_number"
                        conditionField="conditions"
                        enableLink={true}
                        sourceField="customer_source"
                    />
                )
            }
        },
        {
            title: 'Điện thoại',
            dataIndex: 'phone',
            width: '120px',
            key: 'phone',
            render: (text, record) => {
                return (
                    <PhoneNumberList phones={record.customer?.phones} relatedPhones={record.customer?.relationships} />
                )
            }
        },
        {
            title: 'Ngày sinh',
            dataIndex: 'birthday',
            key: 'birthday',
            width: 120,
            render: (birthday, record) => (
                <BirthdayCell birthday={record.customer?.birthday} age={record.customer?.age} />
            ),
        },
        {
            title: 'Phụ trách buổi hẹn',
            key: 'staffs',
            width: 300,
            render: (_, record) => (
                <AppointmentStaffCell record={record} collapseSingleDoctor />
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            width: '100px',
            render: (_, record) => (
                <AppointmentStatusButton
                    record={record}
                    onClick={() => openStatusUpdateModal(record.id, true)}
                />
            )
        },
        {
            title: 'Ghi chú',
            dataIndex: 'note',
            key: 'note',
            width: '250px',
            render: (text, record) => {
                return (<>{HtmlParser(text)}</>)
            }
        },
        {
            title: 'Phiếu điều trị',
            dataIndex: 'treatment_note',
            key: 'treatment_note',
            width: '350px',
            render: (_, record) => renderTreatmentTicket(record)
        },
        {
            title: 'Lịch hẹn tiếp theo',
            dataIndex: 'next_appointment',
            key: 'next_appointment',
            width: '190px',
            render: (nextAppointment) => renderNextAppointment(nextAppointment)
        },
        {
            title: 'Dịch vụ',
            key: 'services',
            width: 320,
            render: (_, record) => (
                <AppointmentServicesCell services={record?.services || []} />
            ),
        },
        {
            title: 'Actions',
            align: 'center',
            width: '250px',
            dataIndex: 'actions',
            key: 'actions',
            render: (text, record) => {
                return (
                    <Space>
                        <Button
                            type="primary"
                            size='small'
                            disabled={!checkPermission('appointment', 'detail')}
                            icon={<EyeOutlined />}
                            onClick={() => onDetail(record.id)}
                        >
                            Xem chi tiết
                        </Button>
                        <Button
                            type="primary"
                            size='small'
                            disabled={!checkPermission('appointment', 'update')}
                            icon={<EditOutlined />}
                            onClick={() => onEdit(record.id)}
                        >
                            Sửa
                        </Button>
                    </Space>
                );
            }
        }
    ];
